import { Meta } from '@/src/components/common/Meta';
import { EventRulesModal } from '@/src/components/modals/EventRulesModal';
import { useEventRules } from '@/src/hooks/store/useEventRules';
import { useCampaigns } from '@/src/store/campaigns.context';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useJamChallenge } from '@/src/store/jam-challenge.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { useUser } from '@/src/store/user.context';
import { getErrorMessage } from '@/src/utils/errors.utils';
import { isDateInThePast } from '@/src/utils/event-campaign-common.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { FlashbarType } from '@/src/utils/notification.utils';
import { Button, ContentLayout, Header, SpaceBetween } from '@amzn/awsui-components-react';
import NiceModal from '@ebay/nice-modal-react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingBar } from '../../common/LoadingBar';
import JamCampaignRules from '../../ui/molecules/JamCampaignRules/JamCampaignRules';
import GlobalInfo from '../../ui/molecules/MyJams/JamChallenges/GlobalInfo';
import ChallengeDetails from '../../ui/organisms/MyJams/JamChallenges/ChallengeDetails';
import './JamChallenges.scss';

const JamChallenges = () => {
  const { t } = useTranslation();
  const { addFlashbar, removeFlashbar, addErrorFlashbar, flashbars } = useFlashbars();
  const {
    startCampaignAttempt,
    campaignAttempt,
    handleShowCampaignRules,
    getCampaignEventDetails,
    showCampaignRules,
    campaignStarted,
    handleIsFetchingCampaign,
  } = useCampaigns();
  const { isFetchingJamChallenge, jamChallengeData, loadJamChallengeData } = useJamChallenge();
  const { user } = useUser();
  const { event } = useJamEventDetails();
  const eventRules = useEventRules();

  const isCampaign = jamChallengeData?.type === 'CAMPAIGN_GROUP';
  const eventName = jamChallengeData?.eventName;
  const isExpired = isDateInThePast(campaignAttempt?.deadline || '');
  const isSupportUser = !!event?.supportUser;
  const isAdmin = !!(user?.isSuperAdmin || user?.isEventAdmin);

  useEffect(() => {
    if (isCampaign) {
      return;
    }

    if (!event?.eventName || eventRules.accepted[event.eventName]) {
      return;
    }

    NiceModal.show(EventRulesModal, {
      event,
    });
  }, [eventRules.accepted]);

  const shouldShowJamCampaignRules = useMemo(() => {
    if (!isCampaign) {
      return false;
    }

    if (isSupportUser || isAdmin) {
      return false;
    }

    if (event?.ended) {
      return false;
    }

    // Still loading campaign state; don't flash the rules view over the challenge list
    if (campaignStarted === undefined) {
      return false;
    }

    return showCampaignRules || !campaignStarted || !!event?.notStarted;
  }, [showCampaignRules, campaignStarted, event, user]);

  // Opens for the campaign rules page
  const handleOpenRules = () => {
    handleShowCampaignRules(true);
    removeFlashbar('campaign-attempt-warning');
  };

  // Closes the campaign rules page
  const handleCloseRules = () => {
    handleShowCampaignRules(false);
  };

  // Handles starting a new campaign attempt
  const handleCampaignAttempt = async () => {
    try {
      if (event && eventName && !event.ended) {
        handleIsFetchingCampaign(true);
        await startCampaignAttempt(eventName, false);
        removeFlashbar('campaign-attempt-warning');
        // Fetch the campaign attempt
        await getCampaignEventDetails(event?.eventName, false, true);

        // Reload the challenge data so that the challenges unlock after
        // creating a new campaign attempt
        await loadJamChallengeData({ id: eventName, showLoader: false });
      }
    } catch (err) {
      addErrorFlashbar(getErrorMessage(err));
    } finally {
      handleIsFetchingCampaign(false);
    }
  };

  // Creates the button for the campaign attempt warning banner.
  // Only render the button if a user is allowed to create a new campaign attempt.
  const addFlashbarAction = (): JSX.Element | undefined => {
    if (isCampaign && campaignAttempt && campaignAttempt.allowedAttempts && campaignAttempt.attemptNumber) {
      if (campaignAttempt.allowedAttempts > campaignAttempt.attemptNumber) {
        return <Button onClick={() => void handleCampaignAttempt()}>Start new attempt</Button>;
      }
    }
  };

  if (isFetchingJamChallenge) {
    return <LoadingBar />;
  }

  // Determine if the campaign attempt flashbar has rendered
  const campaignAttemptFlashbardHasRendered =
    flashbars.find((flashbar) => flashbar.id === 'campaign-attempt-warning') !== undefined;
  useEffect(() => {
    if (
      isCampaign &&
      campaignAttempt &&
      campaignAttempt.attemptNumber > 0 &&
      isExpired &&
      !campaignAttemptFlashbardHasRendered &&
      !shouldShowJamCampaignRules &&
      !event?.ended
    ) {
      addFlashbar({
        content: t(i18nKeys.campaigns.headers.campaignDetails.descriptions.deadlineForAttemptPassed, {
          allowedAttempts: campaignAttempt.allowedAttempts,
          currentAttemptNumber: campaignAttempt.attemptNumber,
        }),
        type: FlashbarType.WARNING,
        action: addFlashbarAction(),
        dismissible: false,
        i18nKeys: [],
        id: 'campaign-attempt-warning',
      });
    }
  }, [campaignAttempt, showCampaignRules, shouldShowJamCampaignRules, event]);

  const eventTitle = event?.title ?? t(i18nKeys.general.unknown);
  const pageTitle = `${t(i18nKeys.challenges.title)} | ${eventTitle}`;

  if (shouldShowJamCampaignRules) {
    return (
      <>
        <Meta title={pageTitle} />
        <JamCampaignRules event={event} campaignAttempt={campaignAttempt} handleCloseRules={handleCloseRules} />
      </>
    );
  }

  return (
    <>
      <Meta title={pageTitle} />
      <ContentLayout
        header={
          <Header
            variant="h1"
            description={t(i18nKeys.myJams.challenges.subtitle)}
            actions={
              isCampaign && !isSupportUser && !isAdmin && !event?.ended ? (
                <Button onClick={handleOpenRules} variant="primary" data-testid="openCampaignRulesButton">
                  {t(i18nKeys.campaigns.rules.title)}
                </Button>
              ) : null
            }
          >
            {t(i18nKeys.myJams.challenges.title)}
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="xxl">
          <GlobalInfo />
          <ChallengeDetails />
        </SpaceBetween>
      </ContentLayout>
    </>
  );
};

export default JamChallenges;
